import {
	getImproveAPI,
	CompleteLessonResponse,
	SaveVideoTimeResponse,
	BulkCompleteResponse,
	CompleteLessonBody,
	LessonsControllerSearchParams,
	SearchLessonsResponse,
	LessonsControllerListParams,
	ListLessonsResponse,
	LessonResponse,
	DeleteLessonResponse,
	PostLessonBody,
	PutLessonBody,
	UploadFilesResponse
} from "../gen";
import {useImproveAxios} from "@/app2/shared/hooks";
import {useTriggerPointsEarnedFloater} from "@/app2/shared/contexts/PointsEarnedFloaterContext";
import {resolveAwardedPointsValue} from "@/app2/shared/utils/resolveAwardedPointsValue";
import {useMutation, useQuery, useQueryClient, type QueryClient} from "@tanstack/react-query";
import {QUERY_KEYS} from "./user";
import {USERS_QUERY_KEYS} from "./users";
import {STREAK_QUERY_KEYS} from "./streaks";
import {COURSES_QUERY_KEYS} from "./courses";

/**
 * Shared React Query hooks for lesson-related data
 *
 * Uses generated API client (getImproveAPI) and types from gen.
 */

export const LESSON_QUERY_KEYS = {
	SEARCH: "lessons-search",
	LIST: "lessons-list",
	DETAIL: "lesson-detail"
} as const;

/** Refetch admin curriculum builder data for all courses (module map + curriculum items). */
const invalidateCourseCurriculumBuilderCaches = (queryClient: QueryClient) => {
	queryClient.invalidateQueries({queryKey: [COURSES_QUERY_KEYS.COURSE_MODULES]});
	queryClient.invalidateQueries({queryKey: [COURSES_QUERY_KEYS.CURRICULUM_ITEMS]});
};

/**
 * Get a single lesson by ID (admin)
 */
export const useGetLesson = (lessonId: number | undefined) => {
	const axiosConfig = useImproveAxios();
	const {lessonsControllerGetById} = getImproveAPI();
	return useQuery<LessonResponse>({
		queryKey: [LESSON_QUERY_KEYS.DETAIL, lessonId],
		queryFn: async () => {
			if (!lessonId) throw new Error("Lesson ID is required");
			const response = await lessonsControllerGetById(lessonId, axiosConfig);
			return response.data;
		},
		enabled: !!lessonId,
		staleTime: 30 * 1000
	});
};

/**
 * List lessons for admin table (paginated)
 * Serializes filters to JSON string so the backend receives a single query param (NestJS expects filters as string and parses it).
 */
export const useListLessons = (params?: LessonsControllerListParams) => {
	const axiosConfig = useImproveAxios();
	const {lessonsControllerList} = getImproveAPI();
	return useQuery<ListLessonsResponse>({
		queryKey: [LESSON_QUERY_KEYS.LIST, params],
		queryFn: async () => {
			const raw = params ?? {};
			const serialized: Record<string, unknown> = {...raw};
			if (raw.filters != null && typeof raw.filters === "object") {
				serialized.filters = JSON.stringify(raw.filters);
			}
			const response = await lessonsControllerList(serialized as LessonsControllerListParams, axiosConfig);
			return response.data;
		},
		staleTime: 30 * 1000
	});
};

/**
 * Upload a single file for lesson attachments
 * Returns file metadata including ID for use in existingFileIds
 */
export const useUploadLessonFile = () => {
	const axiosConfig = useImproveAxios();
	const {lessonsControllerUploadFile} = getImproveAPI();

	return useMutation<UploadFilesResponse, Error, File>({
		mutationFn: async (file: File) => {
			// Use generated client - it handles FormData creation automatically
			const response = await lessonsControllerUploadFile(
				{file},
				axiosConfig
			);
			return response.data;
		}
	});
};

/**
 * Variables for {@link useCreateLesson}. Include `publishedAt` on `body` (ISO string or null) for draft vs published create.
 */
export type CreateLessonMutationVariables = {
	body: PostLessonBody;
};

/**
 * Create a new lesson (admin)
 * Files should be uploaded separately using useUploadLessonFile
 */
export const useCreateLesson = () => {
	const axiosConfig = useImproveAxios();
	const queryClient = useQueryClient();
	const {lessonsControllerCreate} = getImproveAPI();
	return useMutation<LessonResponse, Error, CreateLessonMutationVariables>({
		mutationFn: async ({body}) => {
			const createResponse = await lessonsControllerCreate(body, axiosConfig);
			return createResponse.data;
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({queryKey: [LESSON_QUERY_KEYS.LIST]});
			queryClient.invalidateQueries({queryKey: [LESSON_QUERY_KEYS.SEARCH]});
			queryClient.invalidateQueries({queryKey: [LESSON_QUERY_KEYS.DETAIL, data.id]});
			invalidateCourseCurriculumBuilderCaches(queryClient);
		}
	});
};

/**
 * Update a lesson (admin)
 * Files should be uploaded separately using useUploadLessonFile
 */
export const useUpdateLesson = (lessonId: number) => {
	const axiosConfig = useImproveAxios();
	const queryClient = useQueryClient();
	const {lessonsControllerUpdate} = getImproveAPI();
	return useMutation<LessonResponse, Error, PutLessonBody>({
		mutationFn: async (body) => {
			const response = await lessonsControllerUpdate(lessonId, body, axiosConfig);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({queryKey: [LESSON_QUERY_KEYS.DETAIL, lessonId]});
			queryClient.invalidateQueries({queryKey: [LESSON_QUERY_KEYS.LIST]});
			queryClient.invalidateQueries({queryKey: [LESSON_QUERY_KEYS.SEARCH]});
			invalidateCourseCurriculumBuilderCaches(queryClient);
		}
	});
};

/**
 * Delete a lesson (admin). Soft delete by default.
 */
export const useDeleteLesson = () => {
	const axiosConfig = useImproveAxios();
	const queryClient = useQueryClient();
	const {lessonsControllerDelete} = getImproveAPI();
	return useMutation<
	DeleteLessonResponse,
	Error,
	{lessonId: number; hardDelete?: boolean}
	>({
		mutationFn: async ({lessonId, hardDelete}) => {
			const response = await lessonsControllerDelete(
				lessonId,
				{hard: hardDelete ? "true" : "false"},
				axiosConfig
			);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({queryKey: [LESSON_QUERY_KEYS.LIST]});
			queryClient.invalidateQueries({queryKey: [LESSON_QUERY_KEYS.SEARCH]});
			invalidateCourseCurriculumBuilderCaches(queryClient);
		}
	});
};

/**
 * Mark a lesson enrollment as complete
 */
export const useCompleteLesson = () => {
	const axiosConfig = useImproveAxios();
	const queryClient = useQueryClient();
	const {triggerPointsFloater} = useTriggerPointsEarnedFloater();
	const {lessonsControllerCompleteLesson} = getImproveAPI();

	return useMutation<
	CompleteLessonResponse,
	Error,
	{enrollmentId: number; videoTime?: number; videoLength?: number}
	>({
		mutationFn: async ({enrollmentId, videoTime, videoLength}) => {
			const body: CompleteLessonBody = {};
			if (videoTime !== undefined) body.videoTime = videoTime;
			if (videoLength !== undefined) body.videoLength = videoLength;
			const response = await lessonsControllerCompleteLesson(
				enrollmentId,
				body,
				axiosConfig
			);
			return response.data;
		},
		onSuccess: (data) => {
			const points = resolveAwardedPointsValue(data.awardedPoints);
			if (points != null) {
				triggerPointsFloater(points);
			}

			queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ME]});
			queryClient.invalidateQueries({queryKey: [QUERY_KEYS.FEED]});
			queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ENROLLED_COURSES]});
			queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ACTIVE_COURSES]});
			queryClient.invalidateQueries({queryKey: [STREAK_QUERY_KEYS.STREAK_INFO]});
			queryClient.invalidateQueries({queryKey: [COURSES_QUERY_KEYS.CURRICULUM]});
		}
	});
};

/**
 * Save video playback position for resume functionality
 */
export const useSaveVideoTime = () => {
	const axiosConfig = useImproveAxios();
	const {lessonsControllerSaveVideoTime} = getImproveAPI();

	return useMutation<
	SaveVideoTimeResponse,
	Error,
	{enrollmentId: number; time: number; videoLength?: number}
	>({
		mutationFn: async ({enrollmentId, time, videoLength}) => {
			const response = await lessonsControllerSaveVideoTime(
				enrollmentId,
				{time, videoLength},
				axiosConfig
			);
			return response.data;
		}
	});
};

/**
 * Admin: Bulk complete lessons for a user
 */
export const useBulkCompleteLessons = () => {
	const axiosConfig = useImproveAxios();
	const queryClient = useQueryClient();
	const {lessonsControllerBulkComplete} = getImproveAPI();

	return useMutation<
	BulkCompleteResponse,
	Error,
	{userId: number; lessonEnrollmentIds: number[]; date: string; timezone?: string}
	>({
		mutationFn: async ({userId, lessonEnrollmentIds, date, timezone}) => {
			const response = await lessonsControllerBulkComplete(
				{userId, lessonEnrollmentIds, date, timezone},
				axiosConfig
			);
			return response.data;
		},
		onSuccess: (_, {userId}) => {
			queryClient.invalidateQueries({
				queryKey: [USERS_QUERY_KEYS.USER_FEED, userId]
			});
			queryClient.invalidateQueries({
				queryKey: [USERS_QUERY_KEYS.USER_OVERDUE_LESSONS, userId]
			});
			queryClient.invalidateQueries({
				queryKey: [USERS_QUERY_KEYS.USER_ACTIVE_COURSES, userId]
			});
			queryClient.invalidateQueries({
				queryKey: [USERS_QUERY_KEYS.USER_RECENT_ACTIVITY, userId]
			});
		}
	});
};

/** Extended search params for curriculum builder (server-side filters, pagination, extendedAccess) */
export type SearchLessonsParams = LessonsControllerSearchParams & {
	extendedAccess?: boolean;
	page?: number;
	pageSize?: number;
	order?: string;
	orderBy?: string;
	filters?: string;
};

/** Paginated search response (when API returns totalCount, page, pageSize) */
export type SearchLessonsResponsePaginated = SearchLessonsResponse & {
	totalCount?: number;
	page?: number;
	pageSize?: number;
};

/**
 * Search lessons for curriculum builder.
 * Supports server-side filters, sort, pagination, and extended access (current + extended cohorts).
 */
export const useSearchLessons = (params?: SearchLessonsParams) => {
	const axiosConfig = useImproveAxios();
	const {lessonsControllerSearch} = getImproveAPI();

	return useQuery<SearchLessonsResponsePaginated>({
		queryKey: [LESSON_QUERY_KEYS.SEARCH, params],
		queryFn: async () => {
			const response = await lessonsControllerSearch(
				(params ?? {}) as LessonsControllerSearchParams,
				axiosConfig
			);
			return response.data as SearchLessonsResponsePaginated;
		},
		staleTime: 30 * 1000
	});
};
